import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Collapse,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentsIcon from '@mui/icons-material/Payments';
import dayjs from 'dayjs';

const PaymentCard = ({ payment }) => {
    const { t } = useTranslation("global");
    const [expanded, setExpanded] = useState(false);

    const formatCurrency = (value, currency) => {
        const cur = (currency || 'COP').toUpperCase().trim();
        
        let numVal = typeof value === 'string' ? parseFloat(value.replace(/,/g, '.')) : Number(value);
        if (isNaN(numVal)) numVal = 0;

        return numVal.toLocaleString('es-CO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'currency',
            currency: cur,
            currencyDisplay: 'code'
        });
    };

    const formatDate = (date) => {
        if (!date) return "";
        return dayjs(date).format('DD/MM/YYYY');
    };

    const statusColor = 'success';
    const statusLabel = t("pagos.pagado") || "Pagado";

    return (
        <Card
            elevation={2}
            sx={{
                mb: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                },
                borderLeft: `4px solid #22C55E`
            }}
        >
            <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PaymentsIcon sx={{ color: '#BD1F34', fontSize: 24 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>
                                {t("pagos.aviso") || "Aviso de Pago"}: {payment.avisoPago}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16 }} />
                            {t("pagos.fecha") || "Fecha"}: {formatDate(payment.fechaAviso)}
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            backgroundColor: '#F3F4F6',
                            '&:hover': { backgroundColor: '#E5E7EB' }
                        }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{t("pagos.total_pagado") || "Total Pagado"}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#16A34A' }}>
                            {formatCurrency(payment.importeTotal, payment.monedaPago)}
                        </Typography>
                    </Grid>

                </Grid>

                {/* Expanded Details - Invoices Table */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />

                    <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#F9FAFB' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("pagos.referencia") || "Ref. Externa / Factura"}</TableCell>
                                    <TableCell align="right">{t("pagos.importe_pago") || "Importe Pago"}</TableCell>
                                    <TableCell align="right">{t("pagos.retencion_aplicada") || "Retención Aplicada"}</TableCell>
                                    <TableCell align="right">{t("pagos.descuento") || "Descuento Concedido"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(payment.facturasPagadas || []).map((inv, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                            {inv.referenciaFactura || inv.idDocumentoOriginal || "-"}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#000000', fontWeight: 500 }}>
                                            {formatCurrency(inv.importeFactura, inv.monedaFactura || payment.monedaPago)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#000000', fontWeight: 500 }}>
                                            {formatCurrency(inv.valorRetencion || 0, inv.monedaFactura || payment.monedaPago)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#000000', fontWeight: 500 }}>
                                            {inv.descuentoProntoPago ? formatCurrency(inv.descuentoProntoPago, inv.monedaFactura || payment.monedaPago) : formatCurrency(0, inv.monedaFactura || payment.monedaPago)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default PaymentCard;
